import React, { useState, useContext, useEffect } from "react";
import {
  makeStyles,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  InputBase,
  IconButton,
  Button,
  Divider,
} from "@material-ui/core";
import ChatIcon from "@material-ui/icons/Chat";
import ContactsIcon from "@material-ui/icons/Contacts";
import PersonIcon from "@material-ui/icons/Person";
import SearchIcon from "@material-ui/icons/Search";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import NewTicketModal from "../../components/NewTicketModal";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => ({
  bottomNav: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1200,
    borderTop: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 -2px 8px rgba(0,0,0,0.08)",
    height: 56,
    "& .MuiBottomNavigationAction-root": {
      minWidth: 60,
      padding: "4px 0",
      color: theme.palette.text.secondary,
      "&.Mui-selected": {
        color: theme.palette.primary.main,
      },
    },
    "& .MuiBottomNavigationAction-label": {
      fontSize: "0.68rem",
      "&.Mui-selected": {
        fontSize: "0.72rem",
      },
    },
  },
  panelContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
  },
  searchContainer: {
    padding: "8px 12px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  searchInput: {
    flex: 1,
    backgroundColor: theme.mode === "light" ? "#f1f3f4" : "#424242",
    borderRadius: 20,
    padding: "6px 16px",
    fontSize: 14,
  },
  contactList: {
    flex: 1,
    overflowY: "auto",
    overscrollBehavior: "contain",
    WebkitOverflowScrolling: "touch",
    padding: 0,
  },
  contactItem: {
    minHeight: 56,
    "&:active": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  profileContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    padding: 24,
    gap: 16,
  },
  profileAvatar: {
    width: 80,
    height: 80,
    border: `3px solid ${theme.palette.primary.main}`,
  },
  profileName: {
    fontWeight: 600,
    fontSize: 18,
  },
  profileEmail: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  logoutButton: {
    marginTop: 24,
    borderRadius: 8,
    textTransform: "none",
    color: "#ef4444",
    borderColor: "#ef4444",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    color: theme.palette.text.secondary,
    padding: 32,
  },
}));

const MobileBottomNav = ({ activeTab, onTabChange, visible }) => {
  const classes = useStyles();
  const { user, handleLogout } = useContext(AuthContext);
  const [contactSearch, setContactSearch] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [profileUrl, setProfileUrl] = useState(null);

  useEffect(() => {
    const companyId = user?.companyId;
    if (companyId) {
      const savedProfileImage = localStorage.getItem("profileImage");
      const img = savedProfileImage || user.profileImage;
      if (img) {
        setProfileUrl(`${backendUrl}/public/company${companyId}/user/${img}`);
      } else {
        setProfileUrl(`${backendUrl}/public/app/noimage.png`);
      }
    }
  }, [user?.companyId, user?.profileImage]);

  // Search contacts
  useEffect(() => {
    if (activeTab !== 1 || !contactSearch || contactSearch.length < 2) {
      if (!contactSearch) setContacts([]);
      return;
    }

    const delayDebounce = setTimeout(async () => {
      setContactsLoading(true);
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: contactSearch, pageNumber: 1 },
        });
        setContacts(data.contacts);
      } catch (err) {
        toastError(err);
      }
      setContactsLoading(false);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [contactSearch, activeTab]);

  const handleContactClick = (contact) => {
    setSelectedContact(contact);
    setNewTicketModalOpen(true);
  };

  const handleCloseNewTicketModal = () => {
    setNewTicketModalOpen(false);
    setSelectedContact(null);
  };

  if (!visible) return null;

  const renderContactsPanel = () => (
    <div className={classes.panelContainer}>
      <div className={classes.searchContainer}>
        <InputBase
          className={classes.searchInput}
          placeholder="Buscar contato..."
          value={contactSearch}
          onChange={(e) => setContactSearch(e.target.value)}
          startAdornment={
            <SearchIcon style={{ color: "#999", marginRight: 8, fontSize: 20 }} />
          }
        />
      </div>
      <List className={classes.contactList}>
        {contacts.length === 0 && !contactsLoading && (
          <div className={classes.emptyState}>
            <ContactsIcon style={{ fontSize: 48, opacity: 0.3, marginBottom: 8 }} />
            <Typography variant="body2">
              {contactSearch.length < 2
                ? "Digite ao menos 2 caracteres"
                : "Nenhum contato encontrado"}
            </Typography>
          </div>
        )}
        {contacts.map((contact) => (
          <ListItem
            key={contact.id}
            button
            className={classes.contactItem}
            onClick={() => handleContactClick(contact)}
          >
            <ListItemAvatar>
              <Avatar
                src={contact.profilePicUrl}
                alt={contact.name}
              />
            </ListItemAvatar>
            <ListItemText
              primary={contact.name}
              secondary={contact.number}
            />
            <IconButton size="small" style={{ color: "#25D366" }}>
              <WhatsAppIcon />
            </IconButton>
          </ListItem>
        ))}
      </List>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={handleCloseNewTicketModal}
        initialContact={selectedContact}
      />
    </div>
  );

  const renderProfilePanel = () => (
    <div className={classes.profileContainer}>
      <Avatar
        className={classes.profileAvatar}
        src={profileUrl}
        alt={user.name}
      />
      <Typography className={classes.profileName}>
        {user.name}
      </Typography>
      <Typography className={classes.profileEmail}>
        {user.email}
      </Typography>
      <Typography variant="body2" style={{ opacity: 0.6 }}>
        {user.company?.name}
      </Typography>
      <Button
        variant="outlined"
        className={classes.logoutButton}
        onClick={handleLogout}
      >
        {i18n.t("mainDrawer.appBar.user.logout")}
      </Button>
    </div>
  );

  return (
    <>
      {/* Panel content above bottom nav */}
      {activeTab === 1 && renderContactsPanel()}
      {activeTab === 2 && renderProfilePanel()}

      {/* Bottom navigation bar */}
      <Paper className={classes.bottomNav} elevation={3}>
        <BottomNavigation
          value={activeTab}
          onChange={(event, newValue) => onTabChange(newValue)}
          showLabels
          style={{ height: "100%" }}
        >
          <BottomNavigationAction label="Conversas" icon={<ChatIcon />} />
          <BottomNavigationAction label="Contatos" icon={<ContactsIcon />} />
          <BottomNavigationAction label="Perfil" icon={<PersonIcon />} />
        </BottomNavigation>
      </Paper>
    </>
  );
};

export default MobileBottomNav;
